import { apiClient } from "@/api/api-client";
import createBaseService from "@/api/base-service";

export interface PendingUser {
	id: string;
	first_name: string;
	last_name: string;
	email: string;
	approval_status: "pending" | "approved" | "rejected";
	created_at: string;
	approved_at?: string;
	approved_by?: {
		id: string;
		first_name: string;
		last_name: string;
		email: string;
	};
	approval_notes?: string;
	disabled_at?: string;
	roles?: { id: number; name: string }[];
}

export interface UserSession {
	id: string;
	user_agent: string;
	ip_address: string;
	last_activity: string;
	user_id?: string;
}
export interface UserApprovalFilters {
	search?: string;
	status?: "pending" | "approved" | "rejected";
	per_page?: number;
	page?: number;
}

class UserApprovalService {
	private baseService = createBaseService<PendingUser>("/user-approvals");

	/**
	 * Get pending users for approval
	 */
	async getPendingUsers(filters: UserApprovalFilters = {}) {
		return this.baseService.list(filters);
	}

	/**
	 * Get all users with approval status
	 */
	async getAllUsers(filters: UserApprovalFilters = {}) {
		const response = await apiClient.get<{ data: PendingUser[]; meta: any }>("/user-approvals/all", {
			params: filters,
		});
		return response.data;
	}

	/**
	 * Approve a user
	 */
	async approveUser(userId: string, notes?: string, role?: string) {
		const response = await apiClient.post(`/user-approvals/${userId}/approve`, {
			notes: notes || "",
			role: role || "salesperson",
		});
		return response.data;
	}

	/**
	 * Reject a user
	 */
	async rejectUser(userId: string, notes: string) {
		const response = await apiClient.post(`/user-approvals/${userId}/reject`, {
			notes,
		});
		return response.data;
	}

	/**
	 * Toggle user enabled/disabled status
	 */
	async toggleUserStatus(userId: string) {
		const response = await apiClient.post(`/user-approvals/${userId}/toggle-status`);
		return response.data;
	}

	async findById(id: string) {
		const response = await apiClient.get(`/user-approvals/${id}`);
		return response.data;
	}

	async updateUserRole(userId: string, role: string) {
		const response = await apiClient.patch(`/user-approvals/${userId}/role`, { role });
		return response.data;
	}

	async getUserSessions(id: string): Promise<UserSession[]> {
		const response = await apiClient.get(`/user-sessions/${id}/sessions`);
		return response.data;
	}
}

export const userApprovalService = new UserApprovalService();
